import React, { useEffect, useState, useMemo } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import H4 from "../../components/Typography/H4";
import Badge from "../../components/Badge";
import AddEditModal from "./AddEditModal";
import DeleteModal from "./DeleteModal";
import ButtonWithDropdown, {
  OptionTypes,
} from "../../components/ButtonWithDropdown";
import BulkUploadModal from "./BulkUploadModal";
import { Table } from "../../components/table";
import type { ColumnsType } from "antd/es/table";

// Define the Market type
interface Market {
  id: number;
  market_name: string;
  short_name: string;
  currency: string;
  status: boolean;
}

const StockMarket: React.FC = (): React.ReactElement => {
  const [selectedData, setEditData] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openModal, toggleModal] = useState<any>({
    newEdit: false,
    delete: false,
    bulkUpload: false,
  });
  const {
    execute,
    loading,
    response: markets,
  } = useFetchApi(`/api/stock-markets`, {}, "GET");

  const opts: Array<OptionTypes> = [
    { label: "Add New", id: "newEdit" },
    { label: "Bulk Upload", id: "bulkUpload" },
  ];

  const toggle = (e: any, market?: any) => {
    const type = e.target.id;
    console.log("type", type);
    if (market && Object.keys(market).length) {
      setEditData(market);
    }
    if (type) {
      toggleModal({ ...openModal, [type]: !openModal[type] });
    }
    if (openModal[type]) {
      setEditData({});
    }
  };

  const filteredMarkets = useMemo(() => {
    if (!markets || !searchTerm.trim()) {
      return markets || [];
    }
    return markets.filter((market: Market) =>
      market.market_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [markets, searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  const columns: ColumnsType<Market> = [
    {
      title: "ID #",
      dataIndex: "id",
      key: "id",
      align: "left",
      width: 80,
    },
    {
      title: "Market Name",
      dataIndex: "market_name",
      key: "market_name",
      align: "left",
    },
    {
      title: "Short Name",
      dataIndex: "short_name",
      key: "short_name",
      align: "left",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      align: "left",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status: boolean) => (
        <Badge status={status ? "success" : "error"}>
          {status ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 300,
      render: (_, market: Market) => (
        <div className="flex gap-2 justify-center">
          <Button
            fontSize="small"
            variant="primary"
            id="newEdit"
            onClick={(e: any) => toggle(e, market)}
          >
            Edit
          </Button>
          <Button
            fontSize="small"
            variant="error"
            id="delete"
            onClick={(e: any) => toggle(e, market)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    execute();
  }, []);

  return (
    <div className="flex flex-col w-full p-8 bg-white min-h-full">
      <Loader show={loading} />
      <AddEditModal
        data={selectedData}
        status={openModal.newEdit}
        onClose={toggle}
        onSuccess={execute}
      />
      <DeleteModal
        id="delete"
        data={selectedData}
        status={openModal.delete}
        onClose={toggle}
        onSuccess={execute}
      />
      <BulkUploadModal
        data={selectedData}
        status={openModal.bulkUpload}
        onClose={toggle}
        onSuccess={execute}
      />

      <div className="flex justify-between mb-8 text-white items-center relative z-1">
        <H4>Stock Markets</H4>
        <div className="relative z-10">
          <ButtonWithDropdown
            fontSize="small"
            options={opts}
            onClick={toggle}
            variant="primary"
          >
            Add New Market
          </ButtonWithDropdown>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 relative z-0">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search market name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        )}
        {searchTerm && (
          <span className="text-sm text-gray-600">
            Found {filteredMarkets.length} of {markets?.length || 0}
          </span>
        )}
      </div>

      <div className="relative z-0">
        <Table<Market>
          columns={columns}
          dataSource={filteredMarkets}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowHoverable={true}
          bordered={true}
          size="middle"
          nullText="No data available"
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default StockMarket;
